class ImageDefilementHorizontal extends HTMLElement {
  #viewport = null;
  #prevButton = null;
  #nextButton = null;
  #progressThumb = null;
  #image = null;
  #mobileMediaQuery = null;
  #resizeObserver = null;
  #handleBreakpointChange = null;
  #handleScroll = null;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-ref="viewport"]');
    this.#prevButton = this.querySelector('[data-ref="prev"]');
    this.#nextButton = this.querySelector('[data-ref="next"]');
    this.#progressThumb = this.querySelector('[data-ref="progress-thumb"]');
    this.#image = this.querySelector('.image-block__image');

    this.#prevButton?.addEventListener('click', () => this.#scrollBy(-1));
    this.#nextButton?.addEventListener('click', () => this.#scrollBy(1));

    this.#handleScroll = () => this.#updateUi();
    this.#viewport?.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#image?.addEventListener('load', this.#handleScroll);

    this.#resizeObserver = new ResizeObserver(() => this.#updateUi());
    if (this.#viewport) this.#resizeObserver.observe(this.#viewport);
    if (this.#image) this.#resizeObserver.observe(this.#image);

    this.#mobileMediaQuery = window.matchMedia('(max-width: 750px)');
    this.#handleBreakpointChange = () => this.#updateUi();
    this.#mobileMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    this.#updateUi();
  }

  disconnectedCallback() {
    if (this.#viewport && this.#handleScroll) {
      this.#viewport.removeEventListener('scroll', this.#handleScroll);
    }

    if (this.#image && this.#handleScroll) {
      this.#image.removeEventListener('load', this.#handleScroll);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#mobileMediaQuery && this.#handleBreakpointChange) {
      this.#mobileMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    }
  }

  #isMobile() {
    return this.#mobileMediaQuery?.matches ?? false;
  }

  #scrollBy(direction) {
    if (!this.#isMobile() || !this.#viewport) return;

    this.#viewport.scrollBy({
      left: this.#viewport.clientWidth * 0.85 * direction,
      behavior: 'smooth',
    });
  }

  #updateUi() {
    if (!this.#viewport) return;

    if (!this.#isMobile()) {
      this.#viewport.scrollLeft = 0;
      this.#prevButton?.removeAttribute('disabled');
      this.#nextButton?.removeAttribute('disabled');
      this.#updateProgress(0, 0, 1, 1);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.#viewport;
    const maxScroll = scrollWidth - clientWidth;

    if (this.#prevButton) {
      this.#prevButton.disabled = scrollLeft <= 1;
    }

    if (this.#nextButton) {
      this.#nextButton.disabled = maxScroll <= 1 || scrollLeft >= maxScroll - 1;
    }

    this.#updateProgress(scrollLeft, maxScroll, clientWidth, scrollWidth);
  }

  #updateProgress(scrollLeft, maxScroll, clientWidth, scrollWidth) {
    if (!this.#progressThumb) return;

    if (maxScroll <= 1 || scrollWidth <= clientWidth) {
      this.#progressThumb.style.left = '0';
      this.#progressThumb.style.width = '100%';
      return;
    }

    const leftPercent = (scrollLeft / scrollWidth) * 100;
    const widthPercent = (clientWidth / scrollWidth) * 100;

    this.#progressThumb.style.left = `${leftPercent}%`;
    this.#progressThumb.style.width = `${widthPercent}%`;
  }
}

if (!customElements.get('image-defilement-horizontal')) {
  customElements.define('image-defilement-horizontal', ImageDefilementHorizontal);
}
